/** The document metadata Atlas uses, independent of its rendering framework. */
export type * as MetadataRoute from "./page-metadata-routes";

// Metadata crosses server-function boundaries with page loaders. Keep URL
// values as strings so the payload is serializable on either side of navigation.
type MetadataUrl = string;
type OneOrMany<T> = T | T[];

export type MetadataTitle =
  | string
  | { default: string; template?: string }
  | { absolute: string };

export type MetadataImage =
  | MetadataUrl
  | {
      url: MetadataUrl;
      width?: number | string;
      height?: number | string;
      alt?: string;
      type?: string;
    };

export type MetadataIcon =
  | MetadataUrl
  | {
      url: MetadataUrl;
      sizes?: string;
      type?: string;
      media?: string;
    };

type RobotsDirectives = {
  index?: boolean;
  follow?: boolean;
  noarchive?: boolean;
  nosnippet?: boolean;
  noimageindex?: boolean;
  notranslate?: boolean;
  "max-image-preview"?: "none" | "standard" | "large";
  "max-snippet"?: number;
  "max-video-preview"?: number;
};

export type Metadata = {
  title?: MetadataTitle | null;
  description?: string | null;
  metadataBase?: MetadataUrl | null;
  applicationName?: string | null;
  authors?: OneOrMany<{ name?: string; url?: MetadataUrl }> | null;
  creator?: string | null;
  publisher?: string | null;
  alternates?: { canonical?: MetadataUrl | null } | null;
  robots?:
    | string
    | (RobotsDirectives & { googleBot?: string | RobotsDirectives })
    | null;
  formatDetection?: {
    email?: boolean;
    address?: boolean;
    telephone?: boolean;
  } | null;
  icons?: {
    icon?: OneOrMany<MetadataIcon>;
    apple?: OneOrMany<MetadataIcon>;
    shortcut?: OneOrMany<MetadataIcon>;
  } | null;
  manifest?: MetadataUrl | null;
  openGraph?: {
    title?: MetadataTitle;
    description?: string;
    images?: OneOrMany<MetadataImage>;
    locale?: string;
    siteName?: string;
    type?: "website" | "article";
    url?: MetadataUrl;
  } | null;
  twitter?: {
    card?: "summary" | "summary_large_image";
    title?: MetadataTitle;
    description?: string;
    images?: OneOrMany<MetadataImage>;
  } | null;
};

export type Viewport = {
  width?: number | "device-width";
  height?: number | "device-height";
  initialScale?: number;
  minimumScale?: number;
  maximumScale?: number;
  userScalable?: boolean;
  viewportFit?: "auto" | "contain" | "cover";
  interactiveWidget?: "resizes-visual" | "resizes-content" | "overlays-content";
  colorScheme?: string;
  themeColor?: string | Array<{ media?: string; color: string }>;
};

export type PageHeadMeta =
  | { title: string }
  | { name: string; content: string; media?: string }
  | { property: string; content: string };

export type PageHeadLink = {
  rel: string;
  href: string;
  sizes?: string;
  type?: string;
  media?: string;
};

/** Structural match for TanStack Router's route `head` result. */
export type PageHead = { meta: PageHeadMeta[]; links: PageHeadLink[] };

type MetadataMergeOptions = {
  /** Disable only when a page shares the layout's own metadata segment. */
  applyTitleTemplate?: boolean;
};

function titleText(title: MetadataTitle | null | undefined) {
  if (typeof title === "string") return title;
  if (!title) return undefined;
  return "absolute" in title ? title.absolute : title.default;
}

/** Nested metadata objects replace their parent; only the document title composes. */
export function mergePageMetadata(
  parent: Metadata,
  page: Metadata,
  { applyTitleTemplate = true }: MetadataMergeOptions = {},
): Metadata {
  const metadata: Metadata = {
    ...parent,
    ...Object.fromEntries(
      Object.entries(page).filter(([, value]) => value !== undefined),
    ),
  };
  if (page.title === undefined) return metadata;
  if (page.title === null) return { ...metadata, title: null };

  const title = titleText(page.title);
  const template =
    applyTitleTemplate &&
    parent.title &&
    typeof parent.title === "object" &&
    "template" in parent.title
      ? parent.title.template
      : undefined;
  const absolute = typeof page.title === "object" && "absolute" in page.title;
  return {
    ...metadata,
    title:
      title && template && !absolute
        ? template.replace(/%s/g, () => title)
        : title,
  };
}

function asArray<T>(value: OneOrMany<T> | null | undefined): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? value : [value];
}

function metadataUrl(url: MetadataUrl, base?: MetadataUrl | null) {
  return base ? new URL(url, base).toString() : url;
}

function robotsContent(value: string | RobotsDirectives) {
  if (typeof value === "string") return value;
  const directives: string[] = [];
  if (value.index !== undefined) directives.push(value.index ? "index" : "noindex");
  if (value.follow !== undefined) directives.push(value.follow ? "follow" : "nofollow");
  for (const name of ["noarchive", "nosnippet", "noimageindex", "notranslate"] as const) {
    if (value[name]) directives.push(name);
  }
  for (const name of ["max-image-preview", "max-snippet", "max-video-preview"] as const) {
    if (value[name] !== undefined) directives.push(`${name}:${value[name]}`);
  }
  return directives.join(", ");
}

function addSocialImages(
  meta: PageHeadMeta[],
  protocol: "og" | "twitter",
  images: OneOrMany<MetadataImage> | undefined,
  base: MetadataUrl | null | undefined,
) {
  const add = (name: string, content: string | number | undefined) => {
    if (content === undefined) return;
    meta.push(
      protocol === "og"
        ? { property: name, content: String(content) }
        : { name, content: String(content) },
    );
  };
  for (const image of asArray(images)) {
    const descriptor = typeof image === "string" ? { url: image } : image;
    add(`${protocol}:image`, metadataUrl(descriptor.url, base));
    if (protocol === "og") {
      add("og:image:width", descriptor.width);
      add("og:image:height", descriptor.height);
      add("og:image:type", descriptor.type);
    }
    add(`${protocol}:image:alt`, descriptor.alt);
  }
}

/**
 * TanStack deduplicates named meta tags. Render media-qualified theme colors
 * directly in the root document so both light and dark variants survive.
 */
export function toViewportHead(viewport: Viewport): PageHeadMeta[] {
  const meta: PageHeadMeta[] = [];
  const fields = [
    ["width", viewport.width],
    ["height", viewport.height],
    ["initial-scale", viewport.initialScale],
    ["minimum-scale", viewport.minimumScale],
    ["maximum-scale", viewport.maximumScale],
    ["user-scalable", viewport.userScalable === undefined ? undefined : viewport.userScalable ? "yes" : "no"],
    ["viewport-fit", viewport.viewportFit],
    ["interactive-widget", viewport.interactiveWidget],
  ] as const;
  const content = fields
    .filter(([, value]) => value !== undefined)
    .map(([name, value]) => `${name}=${value}`)
    .join(", ");
  if (content) meta.push({ name: "viewport", content });
  if (viewport.colorScheme) meta.push({ name: "color-scheme", content: viewport.colorScheme });
  if (typeof viewport.themeColor === "string") {
    meta.push({ name: "theme-color", content: viewport.themeColor });
  } else {
    for (const theme of viewport.themeColor ?? []) {
      meta.push({ name: "theme-color", content: theme.color, ...(theme.media ? { media: theme.media } : {}) });
    }
  }
  return meta;
}

/** Convert an already public metadata projection into TanStack head descriptors. */
export function toPageHead(
  page: Metadata,
  options: MetadataMergeOptions & { parent?: Metadata; viewport?: Viewport } = {},
): PageHead {
  const metadata = options.parent ? mergePageMetadata(options.parent, page, options) : page;
  const meta: PageHeadMeta[] = [];
  const links: PageHeadLink[] = [];
  const title = titleText(metadata.title);
  if (title) meta.push({ title });
  const addName = (name: string, content: string | undefined | null) => {
    if (content != null) meta.push({ name, content });
  };
  addName("description", metadata.description);
  addName("application-name", metadata.applicationName);
  addName("creator", metadata.creator);
  addName("publisher", metadata.publisher);
  for (const author of asArray(metadata.authors)) {
    addName("author", author.name);
    if (author.url) links.push({ rel: "author", href: author.url });
  }
  if (metadata.formatDetection) {
    const content = Object.entries(metadata.formatDetection)
      .map(([name, enabled]) => `${name}=${enabled ? "yes" : "no"}`)
      .join(", ");
    if (content) addName("format-detection", content);
  }
  if (metadata.robots) {
    addName("robots", robotsContent(metadata.robots));
    if (typeof metadata.robots === "object" && metadata.robots.googleBot) {
      addName("googlebot", robotsContent(metadata.robots.googleBot));
    }
  }
  if (metadata.alternates?.canonical) {
    links.push({ rel: "canonical", href: metadataUrl(metadata.alternates.canonical, metadata.metadataBase) });
  }
  if (metadata.manifest) links.push({ rel: "manifest", href: metadata.manifest });
  for (const [key, rel] of [["icon", "icon"], ["apple", "apple-touch-icon"], ["shortcut", "shortcut icon"]] as const) {
    for (const icon of asArray(metadata.icons?.[key])) {
      const descriptor = typeof icon === "string" ? { url: icon } : icon;
      const { url, ...attributes } = descriptor;
      links.push({ rel, href: url, ...attributes });
    }
  }
  if (metadata.openGraph) {
    const og = metadata.openGraph;
    for (const [property, content] of [
      ["og:title", titleText(og.title)],
      ["og:description", og.description],
      ["og:url", og.url ? metadataUrl(og.url, metadata.metadataBase) : undefined],
      ["og:site_name", og.siteName],
      ["og:locale", og.locale],
      ["og:type", og.type],
    ] as const) {
      if (content !== undefined) meta.push({ property, content });
    }
    addSocialImages(meta, "og", og.images, metadata.metadataBase);
  }
  if (metadata.twitter) {
    const twitter = metadata.twitter;
    addName("twitter:card", twitter.card);
    addName("twitter:title", titleText(twitter.title));
    addName("twitter:description", twitter.description);
    addSocialImages(meta, "twitter", twitter.images, metadata.metadataBase);
  }
  if (options.viewport) meta.push(...toViewportHead(options.viewport));
  return { meta, links };
}
